import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Spinner, Alert, Badge, Form, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Invite a friend to connect (#358). The friend may already be on dyner.z under
// a different address: whoever opens the link and signs in is who connects.
// The server treats every address the same, so this screen never learns
// (and never shows) whether an address has an account.
function InviteFriends() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [needsName, setNeedsName] = useState(false);
  const [success, setSuccess] = useState('');
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchInvites = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/invites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load your invites');
      setInvites(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, token]);

  useEffect(() => {
    // `token` is a dependency on purpose: sign-out must clear this list (#141 class).
    if (!token) { setInvites([]); return; }
    fetchInvites();
  }, [token, fetchInvites]);

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    setNeedsName(false);
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/invites`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (data.detail?.code === 'display_name_required') {
          setNeedsName(true);
          return;
        }
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not send the invite');
      }
      setSuccess(`Invite sent to ${data.recipient_email}.`);
      setEmail('');
      fetchInvites();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleRevoke = async (id) => {
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/invites/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok && response.status !== 404) throw new Error('Could not revoke the invite');
      fetchInvites();
    } catch (err) {
      setError(err.message);
    }
  };

  const pending = invites.filter((i) => i.status === 'pending');
  const accepted = invites.filter((i) => i.status === 'accepted');

  return (
    <div>
      <p className="text-muted">
        Invite someone you know. If they're already on dyner.z they can sign in with whichever
        account they use; if not, they can join. Either way you'll be friends and follow each other.
      </p>

      {needsName && (
        <Alert variant="warning">
          Add a display name to your profile before inviting friends, so they know who the invite
          is from. You can set it in the <strong>Profile Info</strong> tab.
        </Alert>
      )}
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
          {/* #362: dynerz.net is a new sender, so a first invite can land in Spam. */}
          <div className="small mt-1">
            If they don't see it in a few minutes, ask them to check their Spam or Junk folder.
          </div>
        </Alert>
      )}

      <Form onSubmit={handleSend} className="mb-4">
        <InputGroup>
          <Form.Control
            type="email"
            placeholder="Friend's email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Button type="submit" variant="primary" disabled={sending || !email}>
            {sending ? 'Sending...' : 'Send invite'}
          </Button>
        </InputGroup>
      </Form>

      {loading ? (
        <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
      ) : (
        <>
          <h6>Waiting for a reply {pending.length > 0 && <Badge bg="secondary">{pending.length}</Badge>}</h6>
          {pending.length === 0 ? (
            <p className="text-muted">No invites waiting.</p>
          ) : pending.map((invite) => (
            <Card key={invite.id} className="mb-2">
              <Card.Body className="py-2 d-flex justify-content-between align-items-center">
                <div>
                  <div>{invite.recipient_email}</div>
                  <small className="text-muted">Sent {new Date(invite.created_at).toLocaleDateString()}</small>
                </div>
                <Button variant="outline-danger" size="sm" onClick={() => handleRevoke(invite.id)}>
                  Revoke
                </Button>
              </Card.Body>
            </Card>
          ))}

          {accepted.length > 0 && (
            <>
              <h6 className="mt-4">Accepted</h6>
              {accepted.map((invite) => (
                <Card key={invite.id} className="mb-2">
                  <Card.Body className="py-2 d-flex justify-content-between align-items-center">
                    <div>
                      <div>{invite.recipient_email}</div>
                      <small className="text-muted">
                        Accepted {invite.resolved_at ? new Date(invite.resolved_at).toLocaleDateString() : ''}
                      </small>
                    </div>
                    {invite.accepted_by && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => navigate(`/profile/${invite.accepted_by.user_id}`)}
                      >
                        {invite.accepted_by.display_name || `User ${invite.accepted_by.user_id}`}
                      </Button>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default InviteFriends;
